/**
 * useIntentOrder — helper hook for ordering intent-tagged content.
 *
 * Given an array of items each carrying a category/surface field, returns:
 *   - topIntent: the intent with the highest signal weight (or null if below threshold)
 *   - orderedItems: items reordered so those matching topIntent come first,
 *                   followed by the rest (preserves original order within each group)
 *   - mixedItems: one item per unique category, cycled evenly (for default display
 *                 when no topIntent is known)
 *   - hasSignal: true once at least one signal has been recorded
 *
 * @param {Array} items           input list
 * @param {string} categoryField  field name on each item holding the category ("category" | "surface")
 * @param {number} minWeight      threshold to declare a topIntent (default 0.5 — any
 *                                single signal, even a page-view with content_category
 *                                (1pt) or a testimonial click (1.5pt), will activate
 *                                personalization immediately).
 */
import { useMemo } from 'react';
import { useSmartSite } from '../context/SmartSiteContext';

export function useIntentOrder(items, categoryField = 'category', minWeight = 0.5) {
  const smart = useSmartSite();
  const intents = smart?.intents || {};

  return useMemo(() => {
    if (!items || items.length === 0) {
      return { topIntent: null, orderedItems: [], mixedItems: [], hasSignal: false };
    }

    // Determine top intent above threshold
    const entries = Object.entries(intents).sort((a, b) => b[1] - a[1]);
    const topIntent = entries.length && entries[0][1] >= minWeight ? entries[0][0] : null;
    const hasSignal = entries.length > 0 && entries[0][1] > 0;

    // Order: matches-topIntent first, then others (stable within group)
    const orderedItems = topIntent
      ? [
          ...items.filter(i => i[categoryField] === topIntent),
          ...items.filter(i => i[categoryField] !== topIntent),
        ]
      : [...items];

    // Mixed: round-robin one item per unique category
    const byCat = new Map();
    for (const item of items) {
      const cat = item[categoryField] || 'general';
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat).push(item);
    }
    const mixedItems = [];
    let exhausted = false;
    while (!exhausted) {
      exhausted = true;
      for (const list of byCat.values()) {
        if (list.length > 0) {
          mixedItems.push(list.shift());
          exhausted = false;
        }
      }
    }

    return { topIntent, orderedItems, mixedItems, hasSignal };
  }, [items, intents, categoryField, minWeight]);
}

/** Human-friendly labels for intent categories. */
export const INTENT_LABELS = {
  'school-uniforms': 'School Uniforms',
  'custom-embroidery': 'Custom Embroidery',
  'greek-apparel': 'Greek Apparel',
  'business-branding': 'Business Branding',
  'personalized-gifts': 'Personalized Gifts',
  'graduations': 'Graduations',
  'printing': 'Printing & Transfers',
  'general': 'General',
};

/** Categories considered "general/neutral" — not tracked as strong intent signals. */
export const NEUTRAL_CATEGORIES = new Set(['general', null, undefined, '']);
